import { formatRegionPath } from '@/lib/regions'
/**
 * 시설 페이지 SEO 템플릿 빌더.
 *
 * 영구지침: docs/plan/2026-04-22-seo-facility-detail-optimization.md §2
 *
 * 설계 원칙:
 * - Title: {시설명} - {시도} {시군구}{ 동}{시설유형} | 케어밸런스
 *   (동 정보 없으면 동 생략, 글자 수 초과 시 시도 생략 fallback)
 * - Description: 자연어 한 문장 + 롱테일 지역 키워드 + CTA
 *   (구글 snippet 채택률 개선 목적)
 * - Intro: 페이지 DOM 상단에 노출할 1문장 소개 (검색 스니펫 후보)
 */

export interface FacilitySeoInput {
  name: string
  facilityTypeLabel: string // 예: '요양원', '요양병원' (라벨로 변환된 값)
  addressSido: string | null | undefined
  addressSigungu: string | null | undefined
  addressDong: string | null | undefined
  capacity?: number | null
  currentCount?: number | null
  evaluationGrade?: string | null
  hasDementiaCare?: boolean | null
}

/** title 상한: 구글 검색 결과 표시 가용 길이를 감안한 한글 기준 컷오프. */
const TITLE_MAX_LENGTH = 60

/** description 상한: 검색 결과 snippet 약 160자 기준. */
const DESC_MAX_LENGTH = 155

/**
 * 시설 상세 페이지 Title 생성.
 *
 * 예)
 *  - 헤아림요양원 - 경기도 남양주시 다산동 요양원
 *  - 헤아림요양원 - 경기도 남양주시 요양원 (동 정보 없음)
 *  - 헤아림요양원 - 남양주시 다산동 요양원 (긴 시설명 → 시도 생략)
 *
 * SEO 컴포넌트가 뒤에 " | 케어밸런스"를 붙이므로 여기서는 suffix 미포함.
 */
export function buildFacilityTitle(f: FacilitySeoInput): string {
  const sido = f.addressSido?.trim() || ''
  const sigungu = f.addressSigungu?.trim() || ''
  const dong = f.addressDong?.trim() || ''
  const type = f.facilityTypeLabel || '요양시설'

  const region = formatRegionPath(sido, sigungu, dong)
  const withSido = `${f.name} - ${region} ${type}`
  if (withSido.length <= TITLE_MAX_LENGTH - ' | 케어밸런스'.length) {
    return withSido
  }

  // 시도 생략 fallback
  const regionNoSido = [sigungu, dong].filter(Boolean).join(' ')
  const withoutSido = `${f.name} - ${regionNoSido} ${type}`
  if (withoutSido.length <= TITLE_MAX_LENGTH - ' | 케어밸런스'.length) {
    return withoutSido
  }

  // 그래도 초과하면 동까지 생략
  const regionMin = sigungu || sido
  return `${f.name} - ${regionMin} ${type}`
}

/**
 * 시설 상세 페이지 Meta Description 생성.
 * 자연어 한 문장 + 지역(동 단위) 키워드 + CTA.
 *
 * 예)
 *  "경기도 남양주시 다산동에 위치한 헤아림요양원(요양원, 정원 60명, 평가등급 A).
 *   시설 정보·비용·입소 상담 후기를 케어밸런스에서 확인하세요."
 */
export function buildFacilityDescription(f: FacilitySeoInput): string {
  const sido = f.addressSido?.trim() || ''
  const sigungu = f.addressSigungu?.trim() || ''
  const dong = f.addressDong?.trim() || ''
  const type = f.facilityTypeLabel || '요양시설'

  const location = formatRegionPath(sido, sigungu, dong)
  const locationPhrase = location ? `${location}에 위치한 ` : ''

  // 괄호 속 부가 정보: 시설유형, 정원, 평가등급, 치매전담 등
  const meta: string[] = [type]
  if (typeof f.capacity === 'number' && f.capacity > 0) {
    meta.push(`정원 ${f.capacity}명`)
  }
  if (f.evaluationGrade) {
    meta.push(`평가등급 ${f.evaluationGrade}`)
  }
  if (f.hasDementiaCare) {
    meta.push('치매 특화')
  }
  const metaPhrase = meta.length > 0 ? `(${meta.join(', ')})` : ''

  const sentence =
    `${locationPhrase}${f.name}${metaPhrase}. ` +
    `시설 정보·비용·입소 상담 후기를 케어밸런스에서 확인하세요.`

  // 길이 가드 — 넘치면 괄호 정보 축약
  if (sentence.length <= DESC_MAX_LENGTH) return sentence

  const minimal = `${locationPhrase}${f.name}(${type}). 시설 정보·비용·입소 상담 후기를 케어밸런스에서 확인하세요.`
  return minimal.length <= DESC_MAX_LENGTH ? minimal : minimal.slice(0, DESC_MAX_LENGTH - 1) + '…'
}

/**
 * 본문 상단에 표시할 "시설 소개" 1문장.
 * DOM 상단에 자연어 문장을 배치하여 구글이 UI 레이블 대신 이 문장을 snippet으로 채택하도록 유도.
 * 사용자 UX에도 도움(시설 첫인상 요약).
 *
 * 예)
 *  "경기도 남양주시 다산동에 위치한 헤아림요양원은(는) 요양원으로, 정원 60명 · 평가등급 A · 치매 특화 시설입니다.
 *   실제 비용, 인증 후기, 입소 상담을 한 곳에서 확인하세요."
 */
export function buildFacilityIntro(f: FacilitySeoInput): string {
  const sido = f.addressSido?.trim() || ''
  const sigungu = f.addressSigungu?.trim() || ''
  const dong = f.addressDong?.trim() || ''
  const type = f.facilityTypeLabel || '요양시설'
  const location = formatRegionPath(sido, sigungu, dong)
  const locationPhrase = location ? `${location}에 위치한 ` : ''

  const badges: string[] = []
  if (typeof f.capacity === 'number' && f.capacity > 0) {
    badges.push(`정원 ${f.capacity}명`)
  }
  if (typeof f.currentCount === 'number' && f.currentCount >= 0) {
    badges.push(`현원 ${f.currentCount}명`)
  }
  if (f.evaluationGrade) {
    badges.push(`평가등급 ${f.evaluationGrade}`)
  }
  if (f.hasDementiaCare) {
    badges.push('치매 특화')
  }

  const badgePhrase = badges.length > 0 ? `, ${badges.join(' · ')} 시설입니다` : ' 시설입니다'
  const first = `${locationPhrase}${f.name}은(는) ${type}으로${badgePhrase}.`
  const second = `${f.name}의 실제 비용, 인증 후기, 입소 상담을 한 곳에서 확인하세요.`
  return `${first} ${second}`
}
